import type { EmployerFormData } from "../types/status.types";
import { pakistanCellNumberPattern } from "@schemas/validationPatterns";
import type {
	Employer,
	EmploymentActivity,
	LocationOption,
	OutcomeJobType,
	StudentEmploymentPayload,
} from "../services/statusService";

const PAKISTAN_COUNTRY_NAME = "pakistan";

export const CONTACT_NUMBER_VALIDATION_MESSAGE =
	"Please enter a valid mobile number (e.g., 03001234567,+923186468590)";

export const isValidContactNumber = (value?: string) =>
	Boolean(value && pakistanCellNumberPattern.test(value.trim()));

export const EMPLOYMENT_DATE_RANGE_MESSAGE = "End date cannot be before the start date";

export const isValidEmploymentDateRange = (startDate?: string, endDate?: string) => {
	if (!startDate?.trim() || !endDate?.trim()) return true;
	return endDate >= startDate;
};

export const toEmployerSelectOptions = (employers: Employer[]) =>
	employers.map(employer => ({ value: employer.uuid, label: employer.businessName }));

export const toOutcomeJobTypeSelectOptions = (jobTypes: OutcomeJobType[]) =>
	jobTypes.map(jobType => ({ value: jobType.uuid, label: jobType.name }));

export const toLocationSelectOptions = (locations: LocationOption[]) =>
	locations.map(location => ({ value: location.uuid, label: location.name }));

const formatDateForInput = (value?: string | null) => {
	if (!value) return "";
	return value.includes("T") ? value.split("T")[0] : value;
};

const formatIncomeForInput = (value?: string | number | null) => {
	if (value == null || value === "") return "";
	const parsed = typeof value === "string" ? Number.parseFloat(value) : value;
	if (!Number.isFinite(parsed)) return String(value);
	return Number.isInteger(parsed) ? String(parsed) : String(parsed);
};

export const isPakistanCountryName = (countryName?: string) =>
	Boolean(countryName?.trim().toLowerCase() === PAKISTAN_COUNTRY_NAME);

export const findPakistanCountryOption = (countryOptions: { value: string; label: string }[]) =>
	countryOptions.find(option => isPakistanCountryName(option.label));

export const createEmptyEmployerFormData = (
	pakistanCountry?: { value: string; label: string },
): EmployerFormData => ({
	contactPerson: "",
	contactNumber: "",
	startingDate: "",
	employerName: "",
	monthlySalary: "",
	designation: "",
	businessName: "",
	monthlyIncome: "",
	universityName: "",
	degree: "",
	employerBusinessDetailsId: "",
	organizationName: "",
	outcomeJobTypeUuid: "",
	contactEmail: "",
	endDate: "",
	countryUuid: pakistanCountry?.value ?? "",
	countryName: pakistanCountry?.label ?? "",
	districtUuid: "",
	districtName: "",
	districtId: "",
	cityUuid: "",
	cityName: "",
	isNew: true,
});

export const getEmploymentPayloadType = (
	statusApiCode?: string,
): "EMPLOYMENT" | "SELF_EMPLOYMENT" | null => {
	if (statusApiCode === "EMPLOYED") return "EMPLOYMENT";
	if (statusApiCode === "SELF_EMPLOYED") return "SELF_EMPLOYMENT";
	return null;
};

export const isPakistanCountry = (
	countryUuid: string | undefined,
	countryOptions: { value: string; label: string }[],
	countryName?: string,
) => {
	if (isPakistanCountryName(countryName)) return true;
	if (!countryUuid) return false;
	const country = countryOptions.find(option => option.value === countryUuid);
	return isPakistanCountryName(country?.label);
};

export const employmentToFormData = (
	employment: EmploymentActivity,
	statusType: "Employed" | "Self Employed",
): EmployerFormData => {
	const incomeStr = formatIncomeForInput(employment.monthlyIncome ?? employment.salary);
	const employerUuid =
		statusType === "Self Employed"
			? ""
			: employment.employerBusinessDetails?.uuid ||
				(typeof employment.employerBusinessDetailsId === "string"
					? employment.employerBusinessDetailsId
					: "");

	return {
		uuid: employment.uuid,
		id: employment.id,
		employerBusinessDetailsId: employerUuid,
		organizationName:
			statusType === "Self Employed"
				? employment.organizationName || ""
				: employment.organizationName || employment.employerBusinessDetails?.businessName || "",
		outcomeJobTypeUuid: employment.outcomeJobType?.uuid || "",
		contactPerson: employment.contactPersonName || "",
		contactNumber: employment.contactPersonNumber || "",
		contactEmail: employment.contactPersonEmail || "",
		designation: employment.roleTitle || "",
		monthlySalary: statusType === "Employed" ? incomeStr : "",
		monthlyIncome: statusType === "Self Employed" ? incomeStr : "",
		startingDate: formatDateForInput(employment.startDate ?? employment.jobStarted),
		endDate: formatDateForInput(employment.endDate ?? employment.jobEnded),
		countryUuid: employment.country?.uuid || "",
		countryName: employment.country?.name || "",
		districtUuid: employment.district?.uuid || "",
		districtName: employment.district?.name || "",
		districtId: employment.district?.id ? String(employment.district.id) : "",
		cityUuid: employment.city?.uuid || "",
		cityName: employment.city?.name || "",
		employerName: "",
		businessName: "",
		universityName: "",
		degree: "",
	};
};

export const higherStudyToFormData = (study: {
	uuid?: string;
	id?: number;
	instituteName: string;
	contactPersonName: string;
	contactPersonNumber: string;
	degreeName: string;
}): EmployerFormData => ({
	uuid: study.uuid,
	id: study.id,
	universityName: study.instituteName || "",
	degree: study.degreeName || "",
	contactPerson: study.contactPersonName || "",
	contactNumber: study.contactPersonNumber || "",
	employerName: "",
	monthlySalary: "",
	designation: "",
	businessName: "",
	monthlyIncome: "",
	startingDate: "",
});

export const buildStudentEmploymentPayload = (
	formData: EmployerFormData,
	statusType: "Employed" | "Self Employed",
	statusId: string,
	statusApiCode: string | undefined,
	countryOptions: { value: string; label: string }[],
): StudentEmploymentPayload | null => {
	const employmentType = getEmploymentPayloadType(statusApiCode);
	if (!employmentType) return null;

	const incomeStr = statusType === "Employed" ? formData.monthlySalary || "0" : formData.monthlyIncome || "0";
	const isPakistan = isPakistanCountry(formData.countryUuid, countryOptions, formData.countryName);
	const isSelfEmployed = statusType === "Self Employed";

	return {
		type: employmentType,
		outcomeStatusUuid: statusId,
		outcomeJobTypeUuid: formData.outcomeJobTypeUuid || "",
		employerBusinessDetailsId: isSelfEmployed ? "" : formData.employerBusinessDetailsId || "",
		organizationName: formData.organizationName || "",
		contactPersonName: formData.contactPerson || "",
		contactPersonNumber: formData.contactNumber || "",
		contactPersonEmail: formData.contactEmail || "",
		roleTitle: formData.designation || "",
		monthlyIncome: Number.parseFloat(incomeStr.split(",").join("")),
		startDate: formData.startingDate || "",
		endDate: formData.endDate || "",
		countryUuid: formData.countryUuid || "",
		...(isPakistan
			? {
					districtUuid: formData.districtUuid || "",
					cityUuid: formData.cityUuid || "",
				}
			: {}),
	};
};

export const mergeSelectOption = (
	options: { value: string; label: string }[],
	selectedValue?: string,
	selectedLabel?: string,
) => {
	if (!selectedValue || !selectedLabel) return options;
	if (options.some(option => option.value === selectedValue)) return options;
	return [{ value: selectedValue, label: selectedLabel }, ...options];
};

export const parseStudentEmployment = (response: unknown): EmploymentActivity => {
	if (typeof response === "object" && response !== null && "data" in response) {
		const data = (response as { data?: unknown }).data;
		if (typeof data === "object" && data !== null && !Array.isArray(data)) {
			return data as EmploymentActivity;
		}
	}
	return response as EmploymentActivity;
};
